// Text floaters ("PURCHASE APPROVED ✓ −$700M", "SIGNAL LOST"): one SpritePool of atlas characters. Each registry
// entry reserves maxConcurrent labels × its longest text in characters; a label rises and fades over its life.

import { Vector3, type Color } from 'three'
import { InvariantViolation } from '@taxwar/net'
import type { GlyphAtlas } from './atlas'
import { effectsOf, type EffectId, type LabelDef, type LabelText } from './effects'
import type { PointPlacement } from './placement'
import { SlotRange, scaledCount } from './slots'
import { SpritePool } from './SpritePool'
import { easeOut, envelopeAt } from './timing'
import type { VfxUniforms } from './uniforms'

/** Monospace advance as a fraction of the (square) character cell. */
const CHAR_ADVANCE = 0.6
/** Longest amount a floater prints ($9,999,999M). */
const MAX_AMOUNT_DIGITS = 7
const DECIMAL = 10
const DIGIT_ZERO = 0x30
const NO_ROTATION = 0
const FULL_SCALE = 1
const NO_JITTER = 0

function maxLength(text: LabelText): number {
  return text.kind === 'fixed' ? text.text.length : text.prefix.length + MAX_AMOUNT_DIGITS + text.suffix.length
}

function digitCount(amount: number): number {
  let digits = 1
  for (let rest = Math.floor(amount / DECIMAL); rest > 0; rest = Math.floor(rest / DECIMAL)) digits++
  return digits
}

/** Characters of `text` with `amount` printed in; the amount must be a non-negative integer that fits. */
function textLength(text: LabelText, amount: number): number {
  if (text.kind === 'fixed') return text.text.length
  if (!Number.isInteger(amount) || amount < 0) throw new InvariantViolation(`floater amount ${amount} is not a non-negative integer`)
  const digits = digitCount(amount)
  if (digits > MAX_AMOUNT_DIGITS) throw new InvariantViolation(`floater amount ${amount} exceeds ${MAX_AMOUNT_DIGITS} digits`)
  return text.prefix.length + digits + text.suffix.length
}

function charCodeAt(text: LabelText, amount: number, index: number): number {
  if (text.kind === 'fixed') return text.text.charCodeAt(index)
  if (index < text.prefix.length) return text.prefix.charCodeAt(index)
  const digits = digitCount(amount)
  const digit = index - text.prefix.length
  if (digit < digits) return DIGIT_ZERO + (Math.floor(amount / Math.pow(DECIMAL, digits - 1 - digit)) % DECIMAL)
  return text.suffix.charCodeAt(digit - digits)
}

interface LabelRange {
  readonly labels: SlotRange
  /** Sprite slots per label. */
  readonly stride: number
}

export class LabelSystem {
  readonly pool: SpritePool
  private readonly ranges = new Map<EffectId, LabelRange>()
  /** Per label slot (see LabelRange: label slot × stride = first sprite). */
  private readonly defs: (LabelDef | null)[]
  private readonly firstSprites: Int32Array
  private readonly lengths: Int32Array
  private readonly starts: Float64Array
  private readonly anchors: readonly Vector3[]
  private readonly altitudes: Float32Array

  constructor(
    private readonly atlas: GlyphAtlas,
    uniforms: VfxUniforms,
    scale: number,
  ) {
    let labels = 0
    let sprites = 0
    const firstSprites: number[] = []
    for (const [id, def] of effectsOf('label')) {
      const count = scaledCount(def.maxConcurrent, scale)
      const stride = maxLength(def.text)
      this.ranges.set(id, { labels: new SlotRange(labels, count), stride })
      for (let i = 0; i < count; i++) firstSprites.push(sprites + i * stride)
      labels += count
      sprites += count * stride
    }
    this.pool = new SpritePool(sprites, atlas.texture, uniforms)
    this.defs = new Array<LabelDef | null>(labels).fill(null)
    this.firstSprites = Int32Array.from(firstSprites)
    this.lengths = new Int32Array(labels)
    this.starts = new Float64Array(labels)
    this.anchors = Array.from({ length: labels }, () => new Vector3())
    this.altitudes = new Float32Array(labels)
  }

  play(id: EffectId, def: LabelDef, placement: PointPlacement, colour: Color, now: number): void {
    const range = this.ranges.get(id)
    if (range === undefined) throw new InvariantViolation(`label effect ${id} has no reserved slots`)
    const label = range.labels.claim()
    const length = textLength(def.text, placement.amount)
    const first = this.firstSprites[label] as number
    const advance = def.charHeight * CHAR_ADVANCE
    for (let i = 0; i < range.stride; i++) {
      if (i < length) {
        const cell = this.atlas.cellOfChar(charCodeAt(def.text, placement.amount, i))
        this.pool.setLook(first + i, colour, def.intensity, cell, def.charHeight, def.charHeight, (i - (length - 1) / 2) * advance, 0, NO_JITTER, 0)
      }
      this.pool.hide(first + i)
    }
    this.defs[label] = def
    this.lengths[label] = length
    this.starts[label] = now
    ;(this.anchors[label] as Vector3).copy(placement.at)
    this.altitudes[label] = placement.altitude
  }

  update(now: number): void {
    for (let label = 0; label < this.defs.length; label++) {
      const def = this.defs[label]
      if (def === null || def === undefined) continue
      const first = this.firstSprites[label] as number
      const length = this.lengths[label] as number
      const t = (now - (this.starts[label] as number)) / def.duration
      if (t >= 1) {
        for (let i = 0; i < length; i++) this.pool.hide(first + i)
        this.defs[label] = null
        continue
      }
      const anchor = this.anchors[label] as Vector3
      const radius = (this.altitudes[label] as number) + def.height + def.rise * easeOut(t)
      const alpha = envelopeAt(def.envelope, t)
      for (let i = 0; i < length; i++) this.pool.setMotion(first + i, anchor.x * radius, anchor.y * radius, anchor.z * radius, alpha, FULL_SCALE, NO_ROTATION)
    }
    this.pool.upload()
  }

  dispose(): void {
    this.pool.dispose()
  }
}
